/**
 * tracking-leg.ts — which driving leg the live B2C tracking map is choreographing
 * (PLA-287). Pure, framework-free policy so the (branchy) truth table lives in
 * one tested place instead of an inline ternary at the call site.
 *
 * The customer's car makes a round-trip across three driving legs:
 *  - "pickup": phase 3, isReturn=false. HDP transporter drives hub -> customer
 *    (empty) to collect the car. Active destination = customer pin.
 *  - "toHub":  phase 3, isReturn=true. After handover the driver drives the
 *    customer's car customer -> hub for inspection. Active destination = HUB pin.
 *  - "return": phase 6. The inspected car is delivered hub -> customer. Active
 *    destination = customer pin. Phase 6 is ALWAYS this leg regardless of the
 *    isReturn flag (which can lag at the leg's first tick).
 *
 * Non-driving phases (1, 2, 4, 5, 7) never run the choreography, so the leg
 * there is irrelevant — it falls back to the isReturn flag for stability.
 */
import type { TrackingLeg } from "@/services/map/approach-moments"

export type { TrackingLeg }

/**
 * Resolves the active tracking leg from the order phase and the driver's
 * isReturn flag.
 *
 * @param orderPhase numeric orderPhase (1..7)
 * @param isDriverReturning the driver tick's isReturn flag
 */
export function resolveTrackingLeg(orderPhase: number, isDriverReturning: boolean): TrackingLeg {
  // Phase 6 is always the hub -> customer delivery — pin it so a stale/missing
  // isReturn at the leg's first tick can't briefly mis-route it to "pickup".
  if (orderPhase === 6) return "return"
  // Phase 3 + isReturn = the driver has the customer's car, heading to the hub.
  if (orderPhase === 3 && isDriverReturning) return "toHub"
  // Phase 3 outbound (and any non-driving phase): fall back to the flag.
  return isDriverReturning ? "return" : "pickup"
}
